import { ActionIcon, type ActionIconProps } from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import * as React from 'react';

type CopyActionButtonProps = Omit<ActionIconProps, 'onClick' | 'children'> & {
  onCopy: () => void;
  iconSize?: number;
  feedbackDuration?: number;
};

const CopyActionButton: React.FC<CopyActionButtonProps> = ({
  onCopy,
  iconSize = 20,
  feedbackDuration = 1500,
  ...actionIconProps
}) => {
  const [isCopied, setIsCopied] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = React.useCallback(() => {
    onCopy();
    setIsCopied(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsCopied(false);
      timeoutRef.current = null;
    }, feedbackDuration);
  }, [feedbackDuration, onCopy]);

  React.useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  return (
    <ActionIcon {...actionIconProps} onClick={handleCopy}>
      {isCopied ? <IconCheck size={iconSize} /> : <IconCopy size={iconSize} />}
    </ActionIcon>
  );
};

export default React.memo(CopyActionButton);
